import { memo, useMemo } from "react";
import { useLocation, Link as RouterLink, useNavigate } from "react-router-dom";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import { IconButton, Link, LinkProps, Typography } from "@mui/material";
import { IBreadcrumbsCustom } from "../interfaces";
import styles from "../styles/_breadcrumbsCustom.module.scss";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

interface LinkRouterProps extends LinkProps {
  to: string;
  replace?: boolean;
}

function LinkRouter(props: LinkRouterProps) {
  return <Link {...props} component={RouterLink as any} />;
}

export const BreadcrumbsCustom = memo(
  ({ breadcrumbNameMap }: IBreadcrumbsCustom) => {
    const location = useLocation();
    const navigate = useNavigate();
    const currentPath = useMemo(
      () => location.pathname.split("/").filter((x) => x),
      [location.pathname],
    );

    const buildBreadcrumbPath = (
      pathParts: string[],
      currentIndex: number,
    ): string => {
      return "/" + pathParts.slice(0, currentIndex + 1).join("/");
    };

    const buildBreadcrumbPathCom = (
      pathParts: string[],
      currentIndex: number,
    ): string => {
      let path = "/" + pathParts.slice(0, currentIndex + 1).join("/");
      // Replace numbers with parameter names in order
      // First replace third level numbers with :packageId for paquetes-exportados
      path = path.replace(/\/autorizaciones\/paquetes-exportados\/\d+/, "/autorizaciones/paquetes-exportados/:packageId");
      // Then replace other numbers with :value and :value2
      path = path.replace(/\/\d+($|\/)/, "/:value$1");
      path = path.replace(/\/\d+($|\/)/, "/:value2$1");
      return path;
    };

    const getBreadcrumbName = (path: string): string | null => {
      const entry = Object.keys(breadcrumbNameMap).find((key) => key === path);
      return entry ? (breadcrumbNameMap[entry] as string) : null;
    };

    return (
      <Breadcrumbs
        aria-label="breadcrumb"
        className={styles.breadcrumbs}
        separator="/"
      >
        {currentPath.length > 1 && !Object.keys(breadcrumbNameMap).some((key) => key === buildBreadcrumbPathCom(currentPath, currentPath.length - 1)) && (
          <IconButton aria-label="back" onClick={() => navigate(-1)}>
            <ArrowBackIosIcon />
          </IconButton>
        )}
        <LinkRouter className={styles.homeLink} color="inherit" to="/">
          Inicio
        </LinkRouter>
        {currentPath.map((value: string, index: number) => {
          const last: boolean = index === currentPath.length - 1;
          const to: string = buildBreadcrumbPath(currentPath, index);
          const toCom: string = buildBreadcrumbPathCom(currentPath, index);
          const breadcrumbName = getBreadcrumbName(toCom);

          // Skip /autorizaciones segment if we're on a child route with its own entry
          if (to === "/autorizaciones" && currentPath.length > 1) {
            // Check if the current full path has an entry in the map
            const fullPathCom = buildBreadcrumbPathCom(
              currentPath,
              currentPath.length - 1,
            );
            const hasFullEntry = Object.keys(breadcrumbNameMap).some(
              (key) => key === fullPathCom,
            );
            // Also check if the parent path has an entry
            const parentPathCom = buildBreadcrumbPathCom(
              currentPath,
              currentPath.length - 2,
            );
            const hasParentEntry = Object.keys(breadcrumbNameMap).some(
              (key) => key === parentPathCom,
            );
            // Skip /autorizaciones if we have a full entry or parent entry
            if (hasFullEntry || hasParentEntry) {
              return null;
            }
          }

          // Only render if this path has an entry in the breadcrumb map
          if (breadcrumbName) {
            if (last) {
              return (
                <a
                  key={to}
                  className={`${styles.breadcrumbItemLast} ${styles["active"]}`}
                >
                  {breadcrumbName}
                </a>
              );
            }
            return (
              <LinkRouter to={to} key={to} className={styles.breadcrumbItem}>
                {breadcrumbName}
              </LinkRouter>
            );
          }
        })}
      </Breadcrumbs>
    );
  },
);
